import React, { useState, useEffect, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./GoogleCalendar.css";
import DeleteModal from "../DeleteModal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faCopy,
  faLocationDot,
  faUser,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import {
  connectOutlook,
  checkOutlookStatus,
  logoutOutlook,
  listOutlookEvents,
  generateTeamsLink,
  deleteTeamsEvent,
  createOutlookMeeting,
  updateTeamsEvent,
  updateOutlookMeeting,
} from "./outlookClient";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { DeleteRequest } from "../../Utils/hooks/ApiHandler";
import OutlookAddEventModal from "./Outlookaddeventmodal";
import useCommonData from "../../Utils/actions/advanceFilteringAction";
function CalendarComponent({ userId: userIdProp, onLogout,setActiveService } = {}) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const userId = customer?.customer_details?.id;
  const [events, setEvents] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    startTime: "",
    endDate: "",
    endTime: "",
    assignTo: "",
    invitees: [],
  });
  const { contacts } = useCommonData({ contacts: true });
  const resolveInvitees = (invitees) => {
    if (!Array.isArray(invitees) || invitees.length === 0) return [];
    if (typeof invitees[0] === "object") return invitees;
    if (!contacts) return [];
    const idSet = new Set(invitees.map(String));
    return contacts.filter((c) => idSet.has(String(c.id)));
  };
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const calendarRef = useRef(null);
  const [showMini, setShowMini] = useState(false);
  const [isShrunk, setIsShrunk] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [outlookAccount, setOutlookAccount] = useState({
    email: null,
    name: null,
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem("outlook_calendar_selected_date");
    return savedDate ? new Date(savedDate) : new Date();
  });
  const [currentView, setCurrentView] = useState(() => {
    return localStorage.getItem("outlook_calendar_view") || "dayGridMonth";
  });
  const [dayHeaderFormat, setDayHeaderFormat] = useState({
    weekday: "long",
    day: "numeric",
  });
  const viewRangeRef = useRef(null);
  const fetchEvents = useCallback(
    async (startOverride, endOverride) => {
      if (!userId) return;
      setLoadingEvents(true);
      try {
        let start;
        let end;
        if (startOverride) {
          start = startOverride;
          end = endOverride || startOverride;
        } else if (viewRangeRef.current) {
          start = viewRangeRef.current.start;
          end = viewRangeRef.current.end;
        } else {
          const now = selectedDate || new Date();
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }
        const items = await listOutlookEvents(
          userId,
          headers,
          extras,
          customer_id,
          start,
          end,
        );
        setEvents(
          items.map((event) => ({
            ...event,
            color: "#0078D4",
          })),
        );
      } catch (err) {
        console.error("Error fetching Outlook events:", err);
        toast.error("Could not load Outlook events.");
      } finally {
        setLoadingEvents(false);
      }
    },
    [userId, selectedDate],
  );
  useEffect(() => {
    if (!userId) {
      setCheckingStatus(false);
      return;
    }
    (async () => {
      setCheckingStatus(true);
      const status = await checkOutlookStatus(userId);
      setIsLoggedIn(status.connected);
      setOutlookAccount({ email: status.email, name: status.name });
      setCheckingStatus(false);
      if (status.connected) {
        localStorage.setItem("outlook_connected", "true");
        fetchEvents();
      } else {
        localStorage.removeItem("outlook_connected");
      }
    })();
  }, [userId, fetchEvents]);

  useEffect(() => {
    const onFocus = async () => {
      if (!userId) return;
      const status = await checkOutlookStatus(userId);
      setIsLoggedIn(status.connected);
      setOutlookAccount({ email: status.email, name: status.name });
      if (status.connected) {
        localStorage.setItem("outlook_connected", "true");
        fetchEvents();
      } else {
        localStorage.removeItem("outlook_connected");
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId, fetchEvents]);

  const login = () => {
    if (!userId) {
      toast.error("Missing user id — can't start Outlook login.");
      return;
    }
    connectOutlook(userId);
  };
  const signOut = async () => {
    try {
      await logoutOutlook(userId);
    } finally {
      localStorage.removeItem("outlook_connected");
      setIsLoggedIn(false);
      setEvents([]);
      setOutlookAccount({ email: null, name: null });
      setShowProfileMenu(false);
      onLogout?.();
    }
  };
  const handleDateClick = (info) => {
    const clickedDate = new Date(info.dateStr);
    const dateOnly = clickedDate.toISOString().split("T")[0];
    const viewType = info.view.type;
    let startTime, endTime;
    if (viewType === "dayGridMonth") {
      startTime = "09:00";
      endTime = "10:00";
    } else {
      const timeOnly = clickedDate.toTimeString().slice(0, 5);
      const nextHour = new Date(clickedDate.getTime() + 60 * 60 * 1000)
        .toTimeString()
        .slice(0, 5);
      startTime = timeOnly || "09:00";
      endTime = nextHour || "10:00";
    }
    setFormData({
      title: "",
      description: "",
      date: dateOnly,
      startTime,
      endDate: dateOnly,
      endTime,
      assignTo: "",
      invitees: [],
    });
    setIsEditing(false);
    isLoggedIn
      ? setModalOpen(true)
      : toast.warning(
          "You must be logged in with Outlook to create a new event.",
        );
  };

  const saveEvent = async () => {
    const startISO = new Date(
      `${formData.date}T${formData.startTime}`,
    ).toISOString();
    const endISO = new Date(
      `${formData.endDate}T${formData.endTime}`,
    ).toISOString();
    const resolvedInvitees = resolveInvitees(formData.invitees);
    if (isEditing) {
      if (!selectedEvent) {
        toast.error("No meeting selected to edit.");
        return;
      }
      try {
        let meetLink = selectedEvent.meetLink;
        let meetingType = selectedEvent.meetingType;
        let graphEventId = selectedEvent.graphEventId;
        if (graphEventId) {
          try {
            const updated = await updateTeamsEvent({
              eventId: selectedEvent.graphEventId,
              userId,
              title: formData.title,
              description: formData.description,
              startISO,
              endISO,
              invitees: resolvedInvitees,
            });
            meetLink = updated.meetLink || meetLink;
            meetingType =
              updated.meeting_type === "calender_meeting"
                ? "inperson"
                : "virtual";
            graphEventId = updated.eventId || graphEventId;
          } catch (teamsErr) {
            console.error("Error updating Teams meeting:", teamsErr);
            if (teamsErr.code === 401) {
              toast.error("Outlook session expired. Please log in again.");
              setIsLoggedIn(false);
              return;
            }
            toast.warning(
              "Saved to CRM, but the Teams meeting could not be updated. It may show the old time.",
            );
          }
        }
        await updateOutlookMeeting({
          id: selectedEvent.id,
          userId,
          customer_id,
          title: formData.title,
          description: formData.description,
          date: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime,
          meetLink,
          meetingType,
          eventId: graphEventId,
          meetingStatus: selectedEvent.meetingStatus,
          invitees: resolvedInvitees,
          assignTo: formData.assignTo,
          headers,
          extras,
        });
        await fetchEvents();
        setModalOpen(false);
        setViewModalOpen(false);
        setIsEditing(false);
        toast.success("Meeting updated.");
      } catch (err) {
        console.error("Error updating Outlook meeting:", err);
        if (err.code === 401) {
          toast.error("Outlook session expired. Please log in again.");
          setIsLoggedIn(false);
        } else {
          toast.error("Could not update meeting.");
        }
      }
      return;
    }
    try {
      const { meetLink, meeting_type, eventId } = await generateTeamsLink({
        userId,
        title: formData.title,
        description: formData.description,
        startISO,
        endISO,
        invitees: resolvedInvitees,
        headers,
        extras,
      });
      await createOutlookMeeting({
        userId,
        customer_id,
        title: formData.title,
        description: formData.description,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        invitees: resolvedInvitees,
        assignTo: formData.assignTo,
        meetLink,
        eventId,
        meetingType:
          meeting_type === "calender_meeting" ? "inperson" : "virtual",
        headers,
        extras,
      });
      await fetchEvents();
      setModalOpen(false);
      setViewModalOpen(false);
      setIsEditing(false);
      toast.success("Meeting created and added to your Outlook calendar.");
    } catch (err) {
      console.error("Error creating Outlook meeting:", err);
      if (err.code === 401) {
        toast.error("Outlook session expired. Please log in again.");
        setIsLoggedIn(false);
      } else {
        toast.error("Could not create meeting.");
      }
    }
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    setSelectedEvent({
      id: event.id,
      graphEventId: event.extendedProps.graphEventId || event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      htmlLink:
        event.extendedProps.htmlLink || event.extendedProps.meetLink || "N/A",
      meetLink: event.extendedProps.meetLink || null,
      description: event.extendedProps.description || "No description",
      meetingType: event.extendedProps.meetingType || null,
      meetingStatus: event.extendedProps.status || null,
      invitees: event.extendedProps.invitees || [],
      assignTo: event.extendedProps.assignTo || null,
    });
    setViewModalOpen(true);
    clickInfo.jsEvent.preventDefault();
  };
  const handleEditEvent = () => {
    if (!selectedEvent) return;
    const start = new Date(selectedEvent.start);
    const end = new Date(selectedEvent.end);
    const toDateStr = (d) => d.toISOString().split("T")[0];
    const toTimeStr = (d) => d.toTimeString().slice(0, 5);

    setFormData({
      title: selectedEvent.title,
      description:
        selectedEvent.description === "No description"
          ? ""
          : selectedEvent.description,
      date: toDateStr(start),
      startTime: toTimeStr(start),
      endDate: toDateStr(end),
      endTime: toTimeStr(end),
      assignTo: selectedEvent.assignTo || "",
      invitees: selectedEvent.invitees || [],
    });
    setIsEditing(true);
    setViewModalOpen(false);
    setModalOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    setIsLoading(true);
    try {
      await deleteTeamsEvent(selectedEvent.graphEventId, userId);
      await handleDeleteMeeting();
      await fetchEvents();
      setViewModalOpen(false);
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting event:", err);
      toast.error("Could not delete event.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteMeeting = async () => {
    try {
      if (selectedEvent?.id === null) return;
      const delRes = await DeleteRequest(
        `lead/deleteMeeting/${selectedEvent?.id}`,
        { headers },
        extras,
      );
      if (delRes.status === "fail" || delRes.success === false) {
        toast.error(delRes.message);
        return;
      }
      toast.success(delRes.message);
    } catch (error) {
      toast.error("Failed to delete meeting");
    }
  };
  const handleViewChange = (view) => {
    if (view === "timeGridDay") {
      setShowMini(true);
      setIsShrunk(true);
    } else {
      setShowMini(false);
      setIsShrunk(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date);
  };

  const handleDatesSet = (info) => {
    handleViewChange(info.view.type);
    setSelectedDate(info.view.currentStart);
    setCurrentView(info.view.type);
    const rangeStart = info.view.currentStart;
    const rangeEnd = new Date(info.view.currentEnd.getTime() - 1);
    viewRangeRef.current = { start: rangeStart, end: rangeEnd };

    if (isLoggedIn) {
      fetchEvents(rangeStart, rangeEnd);
    }
    if (calendarRef.current) {
      setTimeout(() => {
        calendarRef.current.getApi().updateSize();
      }, 100);
    }
    if (info.view.type === "timeGridWeek") {
      setDayHeaderFormat({ weekday: "long", day: "numeric" });
    } else if (info.view.type === "dayGridMonth") {
      setDayHeaderFormat({ weekday: "long" });
    }
  };

  useEffect(() => {
    if (!showProfileMenu) return;
    const onClickOutside = (e) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showProfileMenu]);

  useEffect(() => {
    localStorage.setItem(
      "outlook_calendar_selected_date",
      selectedDate.toISOString(),
    );
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem("outlook_calendar_view", currentView);
  }, [currentView]);

  useEffect(() => {
    if (calendarRef.current) {
      setTimeout(() => {
        calendarRef.current.getApi().updateSize();
      }, 300);
    }
  }, [showMini]);

  const dateObj = selectedDate;
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayOfWeek = weekdays[dateObj.getDay()];
  const day = dateObj.getDate();
  const monthName = months[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  return (
    <div
      className="calendar-container"
      style={{
        position: "relative",
        minHeight: "90vh",
        background: "transparent",
        boxShadow: "none",
      }}
    >
      {showMini && (
        <div className="mini-calendar px-3 border border-1 rounded-4">
          <div className="d-flex flex-column align-items-start justify-content-start">
            <h2
              style={{
                color: "#0078D4",
                fontSize: "100px",
                fontWeight: 400,
                marginBottom: 0,
                marginTop: 0,
                padding: 0,
              }}
            >
              {day}
            </h2>
            <p
              style={{
                marginTop: 0,
                color: "#0078D4",
                fontWeight: 400,
                marginBottom: "10px",
                paddingLeft: "5px",
              }}
            >
              {dayOfWeek}, {monthName} {year}
            </p>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => handleDateChange(date)}
              inline
            />
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-start">
        <div className="modern-border-card d-flex justify-content-center align-items-center">
          <h3 className="m-0" style={{ fontSize: "22px", fontWeight: 200 }}>
            Outlook Calendar
          </h3>
        </div>

        <div className="controls d-flex align-items-center gap-2">
          {isLoggedIn && (
            <div className="position-relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu((prev) => !prev)}
                title={outlookAccount.email || "Connected Outlook account"}
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  background: "#0078D4",
                  color: "#fff",
                  border: "none",
                }}
              >
                <FontAwesomeIcon icon={faUser} />
              </button>

              {showProfileMenu && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm"
                  style={{
                    top: "44px",
                    right: 0,
                    minWidth: "260px",
                    zIndex: 1000,
                    padding: "14px",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "#0078D4",
                        color: "#fff",
                        flexShrink: 0,
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div style={{ overflow: "hidden" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {outlookAccount.name || "Outlook account"}
                      </div>
                      <div
                        className="text-muted d-flex align-items-center gap-1"
                        style={{ fontSize: "12px" }}
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {outlookAccount.email || "No email available"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="text-muted" style={{ fontSize: "12px" }}>
                    Connected to Outlook Calendar
                  </div>
                </div>
              )}
            </div>
          )}
          { !isLoggedIn &&
            <button
            onClick={() => setActiveService("")}
          >
           Back
          </button>
          }

          <button
            onClick={() => (isLoggedIn ? signOut() : login())}
            disabled={checkingStatus}
          >
            {checkingStatus
              ? "Checking..."
              : isLoggedIn
                ? "Logout"
                : "Login with Outlook"}
          </button>
          {/* <button onClick={fetchEvents} disabled={!isLoggedIn}>
            {loadingEvents ? <span className="loading">Loading Events...</span> : "Refresh Events"}
          </button> */}
          <button
            onClick={() => {
              if (!isLoggedIn) {
                toast.warning(
                  "You must be logged in with Outlook to create a new event.",
                );
                return;
              }
              const now = new Date();
              const dateOnly = now.toISOString().split("T")[0];
              setFormData({
                title: "",
                description: "",
                date: dateOnly,
                startTime: "09:00",
                endDate: dateOnly,
                endTime: "10:00",
              });
              setIsEditing(false);
              setModalOpen(true);
            }}
            disabled={!isLoggedIn}
          >
            +Add Event
          </button>
        </div>
      </div>

      <div
        className={`calendar-wrapper ${isShrunk ? "shrink" : ""} border border-1 rounded-4`}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          dayHeaderFormat={dayHeaderFormat}
          firstDay={1}
          events={events}
          height="80vh"
          editable={false}
          selectable={true}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          allDaySlot={false}
          headerToolbar={{
            right: "prev,today,next",
            left: "title",
            center: "timeGridDay,timeGridWeek,dayGridMonth",
          }}
          datesSet={handleDatesSet}
          dayMaxEventRows={3}
          moreLinkClick="popover"
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            meridiem: "short",
            hour12: true,
          }}
          eventContent={(arg) => (
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={arg.event.title}
            >
              {arg.timeText && <b>{arg.timeText} </b>}
              {arg.event.title}
            </div>
          )}
        />
      </div>

      <OutlookAddEventModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsEditing(false);
        }}
        onSubmit={saveEvent}
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
        customerId={customer_id}
        headers={headers}
        extras={extras}
      />

      {viewModalOpen && selectedEvent && (
        <div className="modal-overlay">
          <div className="view-modal">
            <div className="d-flex justify-content-between align-items-center px-3 pb-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-top">{selectedEvent.title}</Tooltip>
                }
              >
                <h5
                  style={{
                    width: "88%",
                    whiteSpace: "nowrap",
                    overflowX: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "1rem",
                    fontWeight: 600,
                    lineHeight: 1,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {selectedEvent?.title}
                </h5>
              </OverlayTrigger>
              <button
                className="btn-close mb-2"
                style={{ fontSize: "10px" }}
                onClick={() => setViewModalOpen(false)}
              ></button>
            </div>
            <hr className="p-0 m-0" />
            <div className="px-3">
              <div className="event-details">
                <p
                  className="d-flex"
                  style={{ fontWeight: 500, flexDirection: "column" }}
                >
                  <label className="from-label mb-0">Description:</label>{" "}
                  {selectedEvent.description}
                </p>
                <div className="d-flex gap-4">
                  <p className="d-flex flex-column">
                    <label className="from-label mb-0">Start:</label>{" "}
                    {new Date(selectedEvent.start).toLocaleString()}
                  </p>
                  <p className="d-flex flex-column">
                    <label className="from-label mb-0">End:</label>{" "}
                    {new Date(selectedEvent.end).toLocaleString()}
                  </p>
                </div>
                <p className="d-flex flex-column">
                  <label className="from-label mb-0">Meeting Link:</label>
                  {selectedEvent.meetLink ? (
                    <span className="d-flex align-items-center gap-2 mt-1">
                      <a
                        href={selectedEvent.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faVideo} />
                        Open Meeting
                      </a>
                      <FontAwesomeIcon
                        icon={faCopy}
                        role="button"
                        title="Copy link"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedEvent.meetLink);
                          toast.success("Meeting link copied.");
                        }}
                      />
                    </span>
                  ) : (
                    <span className="d-flex align-items-center gap-1 text-muted mt-1">
                      <FontAwesomeIcon icon={faLocationDot} />
                      In-person meeting
                    </span>
                  )}
                </p>
              </div>

              <div className="modal-actions d-flex gap-2">
                <button className="btn btn-default" onClick={handleEditEvent}>
                  Edit Event
                </button>
                <button
                  className="btn btn-default"
                  onClick={() => {
                    setShowDeleteModal(true);
                    setViewModalOpen(false);
                  }}
                >
                  Delete Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteEvent}
          isLoading={isLoading}
          message={"Confirm deleting this event"}
        />
      )}
    </div>
  );
}

export const OutlookCalendar = ({ userId, onLogout,setActiveService }) => (
  <CalendarComponent userId={userId} onLogout={onLogout} setActiveService={setActiveService} />
);
export default OutlookCalendar;
